import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import {
  PageHeaderWithBack,
  StablesQuotesSection,
} from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useStablesMarketQuotes } from "../../../../Hooks/useStablesMarketQuotes";
import {
  fetchBaasStablesAccountLimits,
  fetchBaasStablesWalletById,
  fetchBaasStablesWalletsList,
  fetchBaasWalletBrlBalance,
  findBaasStablesWallet,
  resolveBaasApiWalletId,
} from "../../../../Services/baasStablesWalletApi";
import { formatCurrency } from "../../../../Utils/formatters";
import {
  BAAS_STABLES_WALLET_PATH,
  buildBaasAccountLimitesPath,
  buildBaasStablesWalletDetailPath,
} from "../constants";
import { BaasStablesOperationBodySkeleton } from "../components/BaasSkeleton";
import { parseWalletIdFromSearch } from "./baasStablesOperationUtils";
import { BaasStablesDepositPanel } from "./components/BaasStablesDepositPanel";
import { BaasStablesLimitsBar } from "./components/BaasStablesLimitsBar";
import { BaasStablesOperationBalanceCards } from "./components/BaasStablesOperationBalanceCards";
import { BaasStablesOperationHistoryPanel } from "./components/BaasStablesOperationHistoryPanel";
import { BaasStablesOperationsPanel } from "./components/BaasStablesOperationsPanel";
import { BaasStablesWalletContextCard } from "./components/BaasStablesWalletContextCard";
import { BaasStablesWalletSelector } from "./components/BaasStablesWalletSelector";

/** Same title/subtitle copy as IB Carteira Stables operation pages. */
const OPERATION_CONFIG = {
  deposit: {
    title: "Depositar",
    subTitle: "Selecione o ativo e a rede para depositar stablecoins na sua carteira.",
  },
  buy: {
    title: "Comprar stablecoins",
    subTitle: "Converta reais em USDC ou USDT. Acompanhe suas compras no histórico abaixo.",
  },
  sell: {
    title: "Vender stablecoins",
    subTitle: "Converta USDC ou USDT em reais. Acompanhe suas vendas no histórico abaixo.",
  },
  swap: {
    title: "Swap entre stablecoins",
    subTitle:
      "Troque entre as stables disponíveis na sua carteira. Acompanhe seus swaps no histórico abaixo.",
  },
  withdraw: {
    title: "Sacar stablecoins",
    subTitle:
      "Envie stablecoins para carteira externa ou conta bancária. Acompanhe seus saques no histórico abaixo.",
  },
};

const getBlockMessage = (blockReason) => {
  if (blockReason === "product_disabled") {
    return intl.get("baas.home.capabilities.productDisabled");
  }
  if (blockReason === "permission_denied") {
    return intl.get("baas.home.capabilities.permissionDenied");
  }
  return intl.get("baas.stables.operation.capabilityBlocked");
};

export const BaasStablesOperationPage = ({ operation }) => {
  const history = useHistory();
  const location = useLocation();
  const { selectedInstance } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can, getBlockReason } = useBaasCapabilities();
  const capabilityId = "stables.operate";
  const allowed = can(capabilityId);

  const config = OPERATION_CONFIG[operation];
  const marketQuoteState = useStablesMarketQuotes({
    carouselOnly: true,
    autoRefreshMs: 8000,
  });

  const walletIdFromQuery = useMemo(
    () => parseWalletIdFromSearch(location.search),
    [location.search],
  );

  const [wallets, setWallets] = useState([]);
  const [selectedWalletId, setSelectedWalletId] = useState(walletIdFromQuery);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [entityLimits, setEntityLimits] = useState(null);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  /** Linked fiat BRL available in centavos (same scale as IB Contas / formatCurrency). */
  const [brlBalanceCents, setBrlBalanceCents] = useState(0);
  const [brlBalanceLoading, setBrlBalanceLoading] = useState(false);

  useEffect(() => {
    if (walletIdFromQuery) {
      setSelectedWalletId(walletIdFromQuery);
    }
  }, [walletIdFromQuery]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setWallets([]);
      setSelectedWallet(null);
      setEntityLimits(null);
      return undefined;
    }

    setWalletsLoading(true);
    (async () => {
      try {
        const nextWallets = await fetchBaasStablesWalletsList(selectedInstance);
        if (cancelled) return;
        setWallets(nextWallets);

        const resolvedId = selectedWalletId || nextWallets[0]?.id || "";
        if (!selectedWalletId && resolvedId) {
          setSelectedWalletId(resolvedId);
        }

        const wallet = resolvedId
          ? findBaasStablesWallet(nextWallets, resolvedId) ||
            (await fetchBaasStablesWalletById(selectedInstance, resolvedId))
          : null;
        if (cancelled) return;
        setSelectedWallet(wallet);

        if (wallet?.accountId) {
          try {
            const limits = await fetchBaasStablesAccountLimits(
              selectedInstance,
              wallet.accountId,
              { walletId: resolveBaasApiWalletId(wallet) },
            );
            if (!cancelled) setEntityLimits(limits);
          } catch {
            if (!cancelled) setEntityLimits({ daily: { used: 0, max: 0 }, monthly: { used: 0, max: 0 }, items: [] });
          }
        } else if (!cancelled) {
          setEntityLimits(null);
        }
      } catch {
        if (!cancelled) {
          setWallets([]);
          setSelectedWallet(null);
          setEntityLimits(null);
        }
      } finally {
        if (!cancelled) setWalletsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, selectedWalletId]);

  useEffect(() => {
    let cancelled = false;

    if (operation !== "buy" || !selectedInstance || !selectedWallet?.accountId) {
      setBrlBalanceCents(0);
      setBrlBalanceLoading(false);
      return undefined;
    }

    setBrlBalanceLoading(true);
    (async () => {
      try {
        const cents = await fetchBaasWalletBrlBalance(selectedInstance, selectedWallet);
        if (!cancelled) setBrlBalanceCents(cents);
      } catch {
        if (!cancelled) setBrlBalanceCents(0);
      } finally {
        if (!cancelled) setBrlBalanceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [operation, selectedInstance, selectedWallet]);

  const brlBalanceFormatted = useMemo(
    () => formatCurrency(brlBalanceCents || 0),
    [brlBalanceCents],
  );

  const handleWalletChange = useCallback(
    (walletId) => {
      setSelectedWalletId(walletId);
      const params = new URLSearchParams(location.search);
      if (walletId) {
        params.set("walletId", walletId);
      } else {
        params.delete("walletId");
      }
      const search = params.toString();
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
      });
    },
    [history, location.pathname, location.search],
  );

  const handleBack = useCallback(() => {
    if (selectedWalletId) {
      history.push(buildBaasStablesWalletDetailPath(selectedWalletId));
      return;
    }
    history.push(BAAS_STABLES_WALLET_PATH);
  }, [history, selectedWalletId]);

  if (!config) {
    return <Redirect to={BAAS_STABLES_WALLET_PATH} />;
  }

  // Same header component + order as IB StablesOperationPage (quotes → title/subtitle/Voltar).
  const pageHeader = (
    <PageHeaderWithBack
      title={config.title}
      subTitle={config.subTitle}
      onBack={handleBack}
    />
  );

  if (isLoading || walletsLoading) {
    return (
      <div className="baas-stables-operation min-w-0 w-full">
        <StablesQuotesSection showViewAll={false} quoteState={marketQuoteState} />
        {pageHeader}
        <BaasStablesOperationBodySkeleton />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="baas-stables-operation min-w-0 w-full">
        <PageHeaderWithBack
          title={config.title}
          subTitle={config.subTitle}
          onBack={handleBack}
        />
        <div className="app-card app-empty-state">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {getBlockMessage(getBlockReason(capabilityId))}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="baas-stables-operation min-w-0 w-full">
      <StablesQuotesSection showViewAll={false} quoteState={marketQuoteState} />

      {pageHeader}

      <div className="space-y-5">

      {selectedWallet ? (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="min-w-[14rem] flex-1 sm:max-w-md sm:flex-none">
            <BaasStablesWalletSelector
              wallets={wallets}
              value={selectedWalletId}
              onChange={handleWalletChange}
              variant="inline"
            />
          </div>
        </div>
      ) : null}

      {walletIdFromQuery && !selectedWallet ? (
        <div className="app-card app-empty-state">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {intl.get("baas.stables.operation.walletNotFound", { walletId: walletIdFromQuery })}
          </p>
        </div>
      ) : null}

      {!selectedWallet ? (
        <BaasStablesWalletSelector
          wallets={wallets}
          value={selectedWalletId}
          onChange={handleWalletChange}
          variant="default"
        />
      ) : (
        <>
          <BaasStablesWalletContextCard wallet={selectedWallet} />

          {operation !== "deposit" ? (
            <BaasStablesLimitsBar
              limits={entityLimits}
              operation={operation}
              configureTo={
                selectedWallet?.accountId
                  ? buildBaasAccountLimitesPath(selectedWallet.accountId, "stables")
                  : null
              }
            />
          ) : null}

          <BaasStablesOperationBalanceCards
            wallet={selectedWallet}
            wallets={wallets}
            operation={operation}
            marketQuotes={marketQuoteState.quotes}
            brlBalance={operation === "buy" ? brlBalanceCents : 0}
            brlBalanceFormatted={operation === "buy" ? brlBalanceFormatted : undefined}
            loadingBalance={operation === "buy" ? brlBalanceLoading : false}
          />

          {operation === "deposit" ? (
            <BaasStablesDepositPanel
              wallet={selectedWallet}
              marketQuotes={marketQuoteState.quotes}
            />
          ) : (
            <BaasStablesOperationsPanel
              wallet={selectedWallet}
              wallets={wallets}
              operation={operation}
              marketQuotes={marketQuoteState.quotes}
              marketQuotesLoading={marketQuoteState.loading}
              brlBalance={operation === "buy" ? brlBalanceCents : 0}
              brlBalanceFormatted={operation === "buy" ? brlBalanceFormatted : undefined}
              loadingBalance={operation === "buy" ? brlBalanceLoading : false}
              entityLimits={entityLimits}
              onOperationConfirmed={() => setHistoryRefreshToken((token) => token + 1)}
            />
          )}

          <BaasStablesOperationHistoryPanel
            wallet={selectedWallet}
            operation={operation}
            refreshToken={historyRefreshToken}
          />
        </>
      )}
      </div>
    </div>
  );
};

export const BaasStablesDepositPage = () => <BaasStablesOperationPage operation="deposit" />;
export const BaasStablesBuyPage = () => <BaasStablesOperationPage operation="buy" />;
export const BaasStablesSellPage = () => <BaasStablesOperationPage operation="sell" />;
export const BaasStablesSwapPage = () => <BaasStablesOperationPage operation="swap" />;
export const BaasStablesWithdrawPage = () => <BaasStablesOperationPage operation="withdraw" />;
/** @deprecated Prefer BaasStablesWithdrawPage — same withdraw/saque flow. */
export const BaasStablesTransferPage = BaasStablesWithdrawPage;
